import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Text,
  Textarea,
  tokens,
} from '@fluentui/react-components';
import { Link, useNavigate } from 'react-router-dom';
import {
  CANCEL_REASONS,
  cancelReasonLabel,
  type ClientReview,
  type CompletedOverlap,
  type PeriodEditPlan,
  type ReviewMergeOption,
} from '../../api/reviews';
import { Trans, useTranslation } from '../../i18n';
import { formatInstant } from '../../i18n/dates';
import {
  useCancelReview,
  useDeleteReview,
  useMergeReview,
  useMonthReadiness,
  usePeriodPreview,
  useReinstateReview,
  useUpdateReviewPeriod,
} from '../../hooks/useReviews';
import { formatPeriodRange } from '../../utils/calendarDate';
import {
  describePeriodChange,
  firstDayOf,
  lastDayOf,
  monthOf,
  monthsAsWords,
  runLabel,
  unfinishedAddedMonths,
} from '../../utils/reviewMonths';

/**
 * Month-coverage controls for the review detail page (spec §10.4–§10.6):
 *   - CoverageBanner: "these months were already reviewed elsewhere", with the one-click fix;
 *   - EditPeriodDialog: whole-month period edit that says what it releases before saving;
 *   - CancelReviewDialog: cancel with a required reason (replaces the reasonless "Archive");
 *   - MergeOffer: an adjacent open review of the same client, and whether the two may be one (§10.7);
 *   - PeriodHistoryNote / CancellationNote: the audit trail, readable;
 *   - DeleteReviewDialog / ReinstateOffer: the two admin-only actions (§10.10–§10.11).
 * The server decides every rule; these only explain and submit.
 */

/** The server's error text, without the transport prefix `fetchApi` adds. */
export function apiErrorText(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  const m = /^API error: \d+ - (.*)$/s.exec(raw);
  if (m) {
    try {
      const body = JSON.parse(m[1]);
      if (body && typeof body.error === 'string') return body.error;
    } catch {
      return m[1];
    }
  }
  return raw;
}

const isOpen = (r: ClientReview) => r.status === 'pending' || r.status === 'in-progress';

/**
 * "Jan – Apr 2026 reviewed by Elvia Castro, completed May 12, 2026." Who completed it, else the assignee;
 * the date only when it was recorded. Never a guess.
 */
function overlapSentence(
  t: (key: string, opts?: Record<string, unknown>) => string,
  months: string,
  o: Pick<CompletedOverlap, 'completedByName' | 'reviewerName' | 'completedAt'>
): string {
  const who = o.completedByName ?? o.reviewerName;
  if (!who) return t('editPeriod.overlapAnon', { months });
  return o.completedAt
    ? t('editPeriod.overlap', { months, who, date: formatInstant(o.completedAt, 'dayYear') })
    : t('editPeriod.overlapNoDate', { months, who });
}

/**
 * The facts of a period edit (spec §10.13), from the server's plan: the completed months it overlaps (who,
 * when), what it adds, and each open review it moves. Information only: nothing here disables saving.
 */
export function PeriodEditFacts({ plan }: { plan: PeriodEditPlan }) {
  const { t } = useTranslation();
  if (!plan.allowed) return null;
  const overlaps = plan.overlapsCompleted;
  const moves = plan.adjustments;
  if (overlaps.length === 0 && moves.length === 0) return null;
  const adds = plan.adds.map(r => runLabel(r.start, r.end)).join(t('common.and'));

  return (
    <MessageBar intent="info" style={{ marginTop: '8px' }} data-testid="period-edit-facts">
      <MessageBarBody>
        {overlaps.map(o => (
          <Text block key={o.reviewId}>
            {overlapSentence(t, monthsAsWords(o.months), o)}
          </Text>
        ))}
        {overlaps.length > 0 && (
          <Text block weight="semibold">
            {adds ? t('editPeriod.adds', { range: adds }) : t('editPeriod.addsNothing')}
          </Text>
        )}
        {moves.map(a => (
          <Text block key={a.reviewId} style={{ marginTop: '4px' }}>
            {a.outcome === 'cancelled' || !a.to
              ? t('editPeriod.absorbs', { from: formatPeriodRange(a.from.start, a.from.end) })
              : t('editPeriod.moves', { from: formatPeriodRange(a.from.start, a.from.end), to: formatPeriodRange(a.to.start, a.to.end) })}
            {a.monthsReleased.length > 0 && <> {t('editPeriod.releasesFromOther', { months: monthsAsWords(a.monthsReleased), count: a.monthsReleased.length })}</>}
            {a.workRecorded && <> {t('editPeriod.otherWorkKept')}</>}
          </Text>
        ))}
      </MessageBarBody>
    </MessageBar>
  );
}

// ── Coverage banner ─────────────────────────────────────────────────────────────────────────

export function CoverageBanner({
  review,
  onCancelCovered,
}: {
  review: ClientReview;
  /** Opens the cancel dialog pre-set to "already reviewed in another review". */
  onCancelCovered: () => void;
}) {
  const shorten = useUpdateReviewPeriod();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const c = review.coverage;
  if (!c || c.kind === 'none' || !isOpen(review)) return null;

  const covered = monthsAsWords(c.coveredMonths);
  const covering = c.coveringReviews.map(r => (
    <Link key={r.id} to={`/reviews/${r.id}`}>
      {formatPeriodRange(r.periodStart, r.periodEnd)}
    </Link>
  ));
  const coveringList = covering.flatMap((el, i) => (i === 0 ? [el] : [<span key={`sep-${i}`}>, </span>, el]));

  if (c.kind === 'full') {
    return (
      <MessageBar intent="warning" style={{ marginBottom: '16px' }}>
        <MessageBarBody>
          <MessageBarTitle>{t('coverage.full.title')}</MessageBarTitle>
          <Trans i18nKey="coverage.full.body" values={{ months: covered }} components={{ reviews: <span>{coveringList}</span> }} />
          <div style={{ marginTop: '8px' }}>
            <Button size="small" appearance="primary" onClick={onCancelCovered}>
              {t('coverage.full.cancel')}
            </Button>
          </div>
        </MessageBarBody>
      </MessageBar>
    );
  }

  const remainder = c.remainder.map(r => runLabel(r.start, r.end)).join(t('common.and'));
  const single = c.kind === 'partial-end' ? c.remainder[0] : null;

  // Information, never a requirement (spec §10.13.1): a reviewer may keep a period that overlaps months
  // already reviewed when that is what she reviewed. Shortening stays one click away, as an option.
  return (
    <MessageBar intent="info" style={{ marginBottom: '16px' }} data-testid="coverage-banner">
      <MessageBarBody>
        <MessageBarTitle>{t('coverage.partial.title')}</MessageBarTitle>
        <Trans
          i18nKey="coverage.partial.body"
          count={c.coveredMonths.length}
          values={{ months: covered, remainder, remains: t('coverage.partial.remains', { count: c.remainder.length }) }}
          components={{ reviews: <span>{coveringList}</span> }}
        />
        {c.coveringReviews.map(r => (
          <Text block size={200} key={`by-${r.id}`} style={{ marginTop: '2px' }}>
            {overlapSentence(t, formatPeriodRange(r.periodStart, r.periodEnd), r)}
          </Text>
        ))}
        <Text block size={200} style={{ marginTop: '4px', color: tokens.colorNeutralForeground3 }}>
          {t('coverage.partial.optional')}
        </Text>
        {single ? (
          <div style={{ marginTop: '8px' }}>
            <Button
              size="small"
              appearance="primary"
              disabled={shorten.isPending}
              onClick={async () => {
                setError(null);
                try {
                  await shorten.mutateAsync({
                    id: review.id,
                    body: {
                      periodStart: firstDayOf(single.start),
                      periodEnd: lastDayOf(single.end),
                      reason: `${covered} already reviewed in another review`,
                    },
                  });
                } catch (e) {
                  setError(apiErrorText(e));
                }
              }}
            >
              {t('coverage.partial.shorten', { range: runLabel(single.start, single.end) })}
            </Button>
          </div>
        ) : (
          <Text block size={200} style={{ marginTop: '4px' }}>
            {t('coverage.partial.manual')}
          </Text>
        )}
        {error && (
          <Text block size={200} style={{ color: tokens.colorPaletteRedForeground1, marginTop: '4px' }}>
            {error}
          </Text>
        )}
      </MessageBarBody>
    </MessageBar>
  );
}

// ── Edit period ─────────────────────────────────────────────────────────────────────────────

export function EditPeriodDialog({
  review,
  open,
  onOpenChange,
}: {
  review: ClientReview;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const mutation = useUpdateReviewPeriod();
  const { t } = useTranslation();
  const [startMonth, setStartMonth] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setStartMonth(monthOf(review.periodStart) ?? '');
      setEndMonth(monthOf(review.periodEnd) ?? '');
      setReason('');
      setError(null);
    }
  }, [open, review.periodStart, review.periodEnd]);

  const valid = /^\d{4}-\d{2}$/.test(startMonth) && /^\d{4}-\d{2}$/.test(endMonth) && startMonth <= endMonth;
  const change = useMemo(
    () => (valid ? describePeriodChange(review, startMonth, endMonth) : null),
    [valid, review, startMonth, endMonth]
  );
  const unchanged = !!change && change.added.length === 0 && change.released.length === 0;
  const hasWork =
    review.checklistItems.some(i => i.isChecked) || review.financialDocuments.length > 0 || review.emailsSent.length > 0;
  // DP-READY-2 (warn, never refuse): the months this edit ADDS that are not ready to review yet. Readiness is the
  // strip's own fail-soft request; if it cannot say, only "not yet ended" is claimed, from the Eastern calendar.
  const readiness = useMonthReadiness(open ? review.id : undefined);
  const unfinished = useMemo(
    () =>
      change && !unchanged
        ? unfinishedAddedMonths(change.added, readiness.data?.available ? readiness.data.months : null)
        : [],
    [change, unchanged, readiness.data]
  );
  // The server's plan for exactly these bounds (spec §10.13). It informs; it never disables Save.
  const preview = usePeriodPreview(
    review.id,
    valid && !unchanged ? firstDayOf(startMonth) : null,
    valid && !unchanged ? lastDayOf(endMonth) : null
  );

  const save = async () => {
    setError(null);
    try {
      const result = await mutation.mutateAsync({
        id: review.id,
        body: {
          periodStart: firstDayOf(startMonth),
          periodEnd: lastDayOf(endMonth),
          reason: reason.trim() || undefined,
        },
      });
      const notMoved = result?.adjustmentsFailed?.length ?? 0;
      if (notMoved > 0) {
        // Saved, but a neighbour kept its months: say so instead of closing as if all went to plan.
        setError(t('editPeriod.moveFailed', { count: notMoved }));
        return;
      }
      onOpenChange(false);
    } catch (e) {
      setError(apiErrorText(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => onOpenChange(d.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('editPeriod.title')}</DialogTitle>
          <DialogContent>
            <Text block style={{ marginBottom: '12px', color: tokens.colorNeutralForeground3 }}>
              {t('editPeriod.intro')}
            </Text>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <Field label={t('editPeriod.firstMonth')} required>
                <Input type="month" value={startMonth} onChange={(_, d) => setStartMonth(d.value)} />
              </Field>
              <Field label={t('editPeriod.lastMonth')} required validationMessage={valid || !startMonth || !endMonth ? undefined : t('editPeriod.backwards')}>
                <Input type="month" value={endMonth} onChange={(_, d) => setEndMonth(d.value)} />
              </Field>
            </div>
            {change && !unchanged && (
              <MessageBar intent="info" style={{ marginTop: '12px' }}>
                <MessageBarBody>
                  <Trans i18nKey="editPeriod.willCover" values={{ range: change.covers }} components={{ strong: <strong /> }} />
                  {change.released.length > 0 && (
                    <>
                      {' '}
                      {t('editPeriod.released', { months: monthsAsWords(change.released), count: change.released.length })}
                    </>
                  )}
                </MessageBarBody>
              </MessageBar>
            )}
            {preview.data && !unchanged && <PeriodEditFacts plan={preview.data} />}
            {unfinished.length > 0 && (
              <MessageBar intent="warning" style={{ marginTop: '8px' }} data-testid="period-edit-unfinished">
                <MessageBarBody>
                  <MessageBarTitle>{t('editPeriod.unfinished.title', { count: unfinished.length })}</MessageBarTitle>
                  {unfinished.map(u => (
                    <Text block key={u.month}>
                      {monthsAsWords([u.month])}: {t(`monthReadiness.${u.state}`)}
                      {u.taskSubject ? ` (${u.taskSubject})` : ''}
                    </Text>
                  ))}
                  <Text block size={200} style={{ marginTop: '4px' }}>
                    {t('editPeriod.unfinished.note')}
                  </Text>
                </MessageBarBody>
              </MessageBar>
            )}
            {preview.isError && !unchanged && (
              <Text block size={200} style={{ marginTop: '8px', color: tokens.colorNeutralForeground3 }}>
                {t('editPeriod.previewFailed')}
              </Text>
            )}
            {hasWork && !unchanged && (
              <MessageBar intent="warning" style={{ marginTop: '8px' }}>
                <MessageBarBody>
                  {t('editPeriod.workRecorded')}
                </MessageBarBody>
              </MessageBar>
            )}
            <Field label={t('editPeriod.reason')} style={{ marginTop: '12px' }}>
              <Textarea value={reason} onChange={(_, d) => setReason(d.value)} placeholder={t('editPeriod.reasonPlaceholder')} />
            </Field>
            {error && (
              <Text block style={{ color: tokens.colorPaletteRedForeground1, marginTop: '8px' }}>
                {error}
              </Text>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)}>
              {t('common.close')}
            </Button>
            <Button appearance="primary" disabled={!valid || unchanged || mutation.isPending} onClick={save}>
              {t('editPeriod.save')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ── Cancel ──────────────────────────────────────────────────────────────────────────────────

export function CancelReviewDialog({
  review,
  open,
  onOpenChange,
  initialReason,
  onCancelled,
}: {
  review: ClientReview;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialReason?: string;
  onCancelled?: () => void;
}) {
  const mutation = useCancelReview();
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason(initialReason ?? '');
      setNote('');
      setError(null);
    }
  }, [open, initialReason]);

  const picked = CANCEL_REASONS.find(r => r.value === reason);
  const noteMissing = !!picked?.needsNote && !note.trim();

  const submit = async () => {
    setError(null);
    try {
      await mutation.mutateAsync({ id: review.id, body: { closeReason: reason, note: note.trim() || undefined } });
      onOpenChange(false);
      onCancelled?.();
    } catch (e) {
      setError(apiErrorText(e));
    }
  };

  const months = monthOf(review.periodStart) && monthOf(review.periodEnd)
    ? runLabel(monthOf(review.periodStart)!, monthOf(review.periodEnd)!)
    : t('cancelReview.itsMonths');

  return (
    <Dialog open={open} onOpenChange={(_, d) => onOpenChange(d.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('cancelReview.title')}</DialogTitle>
          <DialogContent>
            <Text block style={{ marginBottom: '12px' }}>
              {t('cancelReview.body', { months })}
            </Text>
            <Field label={t('cancelReview.reason')} required>
              <Dropdown
                placeholder={t('cancelReview.chooseReason')}
                value={picked ? cancelReasonLabel(picked.value) ?? '' : ''}
                selectedOptions={reason ? [reason] : []}
                onOptionSelect={(_, d) => setReason(d.optionValue ?? '')}
              >
                {CANCEL_REASONS.map(r => (
                  <Option key={r.value} value={r.value}>
                    {cancelReasonLabel(r.value) ?? r.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            <Field
              label={picked?.needsNote ? t('cancelReview.noteRequired') : t('cancelReview.note')}
              required={!!picked?.needsNote}
              style={{ marginTop: '12px' }}
            >
              <Textarea value={note} onChange={(_, d) => setNote(d.value)} />
            </Field>
            {error && (
              <Text block style={{ color: tokens.colorPaletteRedForeground1, marginTop: '8px' }}>
                {error}
              </Text>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)}>
              {t('cancelReview.keep')}
            </Button>
            <Button appearance="primary" disabled={!reason || noteMissing || mutation.isPending} onClick={submit}>
              {t('cancelReview.confirm')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ── Audit notes ─────────────────────────────────────────────────────────────────────────────

export function PeriodHistoryNote({ review }: { review: ClientReview }) {
  const { t } = useTranslation();
  const history = review.periodHistory ?? [];
  if (history.length === 0) return null;
  const last = history[history.length - 1];
  const who = last.source === 'autogen-extend' ? t('history.generation') : last.changedByName ?? t('history.unrecorded');

  // This review was moved by ANOTHER review's period edit (spec §10.13.2): say which, who and when.
  if (last.source === 'absorbed-by-review') {
    return (
      <Text block size={200} style={{ color: tokens.colorNeutralForeground3 }} data-testid="period-history-note">
        <Trans
          i18nKey="history.absorbed"
          values={{ range: formatPeriodRange(last.from.start, last.from.end), who, date: formatInstant(last.changedAt, 'dayYear') }}
          components={{ rlink: last.relatedReviewId ? <Link to={`/reviews/${last.relatedReviewId}`} /> : <span /> }}
        />
        {history.length > 1 ? t('history.total', { count: history.length }) : ''}
      </Text>
    );
  }

  return (
    <Text block size={200} style={{ color: tokens.colorNeutralForeground3 }}>
      {/* changedAt is an INSTANT (when someone acted), so it renders in local time. */}
      {t('history.changed', {
        range: formatPeriodRange(last.from.start, last.from.end),
        who,
        date: formatInstant(last.changedAt, 'dayYear'),
      })}
      {last.reason ? ` — ${last.reason}` : ''}
      {last.workRecorded ? t('history.workKept') : ''}
      {history.length > 1 ? t('history.total', { count: history.length }) : ''}
      {last.overlapsCompleted?.length ? (
        <>
          {t('history.overlapped', {
            months: monthsAsWords(last.overlapsCompleted.flatMap(o => o.months)),
          })}
        </>
      ) : null}
      {(last.adjusted ?? []).map(a => (
        <span key={a.reviewId} style={{ display: 'block' }}>
          <Trans
            i18nKey={a.outcome === 'cancelled' || !a.to ? 'history.cancelledOther' : 'history.movedOther'}
            values={{
              from: formatPeriodRange(a.from.start, a.from.end),
              to: a.to ? formatPeriodRange(a.to.start, a.to.end) : '',
            }}
            components={{ rlink: <Link to={`/reviews/${a.reviewId}`} /> }}
          />
        </span>
      ))}
    </Text>
  );
}

export function CancellationNote({ review }: { review: ClientReview }) {
  const { t } = useTranslation();
  if (review.status !== 'cancelled') return null;
  const label = cancelReasonLabel(review.closeReason);
  return (
    <MessageBar intent="info" style={{ marginBottom: '16px' }}>
      <MessageBarBody>
        <MessageBarTitle>{t('cancellation.title')}</MessageBarTitle>
        {label ? `${label}.` : t('cancellation.noReason')}
        {review.cancelledByName ? t('cancellation.by', { name: review.cancelledByName }) : ''}
        {review.cancelledAt ? t('cancellation.on', { date: formatInstant(review.cancelledAt, 'dayYear') }) : ''}
        {review.cancelNote ? ` “${review.cancelNote}”` : ''}
        {review.mergedIntoReviewId && (
          <>
            {' '}
            <Trans
              i18nKey={review.closeReason === 'absorbed-by-review' ? 'cancellation.absorbedBy' : 'cancellation.mergedInto'}
              components={{ rlink: <Link to={`/reviews/${review.mergedIntoReviewId}`} /> }}
            />
          </>
        )}
      </MessageBarBody>
    </MessageBar>
  );
}

// ── Merge (spec §10.7) ──────────────────────────────────────────────────────────────────────

/**
 * The adjacent open reviews of this client, each with the server's verdict. An allowed merge is an
 * offer with a confirmation that says exactly what happens; a refused one says why, in the same
 * sentence the endpoint would answer with, so nobody has to press a button to learn the rule.
 */
export function MergeOffer({ review }: { review: ClientReview }) {
  const [pending, setPending] = useState<ReviewMergeOption | null>(null);
  const { t } = useTranslation();
  if (!isOpen(review) || !review.mergeOptions?.length) return null;

  return (
    <>
      {review.mergeOptions.map((o) =>
        o.allowed ? (
          <MessageBar key={o.otherReviewId} intent="info" style={{ marginBottom: '16px' }}>
            <MessageBarBody>
              <MessageBarTitle>{t('merge.offerTitle')}</MessageBarTitle>
              <Trans
                i18nKey="merge.offerBody"
                values={{ other: formatPeriodRange(o.otherPeriodStart, o.otherPeriodEnd), merged: formatPeriodRange(o.mergedStart!, o.mergedEnd!) }}
                components={{ rlink: <Link to={`/reviews/${o.otherReviewId}`} /> }}
              />
              <div style={{ marginTop: '8px' }}>
                <Button size="small" onClick={() => setPending(o)}>
                  {t('merge.offerButton')}
                </Button>
              </div>
            </MessageBarBody>
          </MessageBar>
        ) : (
          <Text key={o.otherReviewId} block size={200} style={{ color: tokens.colorNeutralForeground3, marginBottom: '12px' }}>
            <Trans
              i18nKey="merge.refused"
              values={{ other: formatPeriodRange(o.otherPeriodStart, o.otherPeriodEnd), reason: o.reason }}
              components={{ rlink: <Link to={`/reviews/${o.otherReviewId}`} /> }}
            />
          </Text>
        ),
      )}
      {pending ? <MergeDialog review={review} option={pending} onClose={() => setPending(null)} /> : null}
    </>
  );
}

function MergeDialog({ review, option, onClose }: { review: ClientReview; option: ReviewMergeOption; onClose: () => void }) {
  const mutation = useMergeReview();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const thisRange = formatPeriodRange(review.periodStart, review.periodEnd);
  const otherRange = formatPeriodRange(option.otherPeriodStart, option.otherPeriodEnd);
  const merged = formatPeriodRange(option.mergedStart!, option.mergedEnd!);
  const thisSurvives = option.survivorId === review.id;
  const [keptRange, goneRange] = thisSurvives ? [thisRange, otherRange] : [otherRange, thisRange];

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('merge.dialogTitle', { merged })}</DialogTitle>
          <DialogContent>
            <Text block>
              {t('merge.keeps', { kept: keptRange, merged })}
            </Text>
            <Text block style={{ marginTop: '8px' }}>
              {t('merge.supersedes', { gone: goneRange })}
            </Text>
            <Field label={t('merge.note')} style={{ marginTop: '12px' }}>
              <Textarea value={reason} onChange={(_, d) => setReason(d.value)} />
            </Field>
            {error ? (
              <Text block size={200} style={{ color: tokens.colorPaletteRedForeground1, marginTop: '8px' }}>
                {error}
              </Text>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              {t('common.back')}
            </Button>
            <Button
              appearance="primary"
              disabled={mutation.isPending}
              onClick={async () => {
                setError(null);
                try {
                  const result = await mutation.mutateAsync({
                    id: review.id,
                    body: { withReviewId: option.otherReviewId, reason: reason.trim() || undefined },
                  });
                  onClose();
                  // This page's review may be the one that was superseded: go to the one that carries on.
                  // programmatic-nav: after a merge, the review that carries on (this one may be gone).
                  if (result.survivor.id !== review.id) navigate(`/reviews/${result.survivor.id}`);
                } catch (e) {
                  setError(apiErrorText(e));
                }
              }}
            >
              {t('merge.confirm')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ── Admin actions (spec §10.10–§10.11) ──────────────────────────────────────────────────────

/**
 * The hard delete. Offered only when the server stamped `deleteOption`, which it does for admins
 * only. A refused delete says why in the server's words and points at Cancel, which keeps the
 * record; an allowed one says plainly that nothing is left behind.
 */
export function DeleteReviewDialog({
  review,
  open,
  onOpenChange,
  onCancelInstead,
}: {
  review: ClientReview;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCancelInstead?: () => void;
}) {
  const mutation = useDeleteReview();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const option = review.deleteOption;
  if (!option) return null;

  const range = formatPeriodRange(review.periodStart, review.periodEnd);
  return (
    <Dialog open={open} onOpenChange={(_, d) => onOpenChange(d.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{option.allowed ? t('deleteReview.title') : t('deleteReview.cannot')}</DialogTitle>
          <DialogContent>
            {option.allowed ? (
              <Text block>
                {t('deleteReview.body', { range, client: review.clientName })}
              </Text>
            ) : (
              <Text block>{option.reason}</Text>
            )}
            {error ? (
              <Text block size={200} style={{ color: tokens.colorPaletteRedForeground1, marginTop: '8px' }}>
                {error}
              </Text>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)}>
              {t('common.back')}
            </Button>
            {isOpen(review) && onCancelInstead ? (
              <Button
                onClick={() => {
                  onOpenChange(false);
                  onCancelInstead();
                }}
              >
                {t('deleteReview.cancelInstead')}
              </Button>
            ) : null}
            {option.allowed ? (
              <Button
                appearance="primary"
                disabled={mutation.isPending}
                style={{ backgroundColor: tokens.colorPaletteRedBackground3 }}
                onClick={async () => {
                  setError(null);
                  try {
                    await mutation.mutateAsync(review.id);
                    onOpenChange(false);
                    // programmatic-nav: after a delete, back to the list; this review no longer exists.
                    navigate('/reviews');
                  } catch (e) {
                    setError(apiErrorText(e));
                  }
                }}
              >
                {mutation.isPending ? t('deleteReview.deleting') : t('deleteReview.confirm')}
              </Button>
            ) : null}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

/**
 * On a cancelled review, an admin may put it back (spec §10.11). Shown only when the server stamped
 * `reinstateOption`. A refused reinstate says why, in the server's words, with no button; an allowed
 * one asks for a reason, which the review's audit record keeps along with the cancellation it undoes.
 */
export function ReinstateOffer({ review }: { review: ClientReview }) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const option = review.reinstateOption;
  if (review.status !== 'cancelled' || !option) return null;

  if (!option.allowed) {
    return (
      <Text block size={200} style={{ color: tokens.colorNeutralForeground3, marginBottom: '12px' }}>
        {t('reinstate.cannot', { reason: option.reason })}
      </Text>
    );
  }
  return (
    <>
      <div style={{ marginBottom: '16px' }}>
        <Button size="small" onClick={() => setOpen(true)}>
          {t('reinstate.offer')}
        </Button>
      </div>
      {open ? <ReinstateDialog review={review} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function ReinstateDialog({ review, onClose }: { review: ClientReview; onClose: () => void }) {
  const mutation = useReinstateReview();
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const range = formatPeriodRange(review.periodStart, review.periodEnd);

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('reinstate.title', { range })}</DialogTitle>
          <DialogContent>
            <Text block>
              {t('reinstate.body', { range, client: review.clientName })}
            </Text>
            <Field label={t('reinstate.why')} required style={{ marginTop: '12px' }}>
              <Textarea value={reason} onChange={(_, d) => setReason(d.value)} />
            </Field>
            {error ? (
              <Text block size={200} style={{ color: tokens.colorPaletteRedForeground1, marginTop: '8px' }}>
                {error}
              </Text>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              {t('common.back')}
            </Button>
            <Button
              appearance="primary"
              disabled={mutation.isPending || !reason.trim()}
              onClick={async () => {
                setError(null);
                try {
                  await mutation.mutateAsync({ id: review.id, body: { reason: reason.trim() } });
                  onClose();
                } catch (e) {
                  setError(apiErrorText(e));
                }
              }}
            >
              {t('reinstate.confirm')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
